// components/event-detail-dialog.component.tsx
import React, { useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { getDisplayEventName, hasProperties, useResolveSchema } from "../utils/schema.util";
import { useSchema } from "../context/SchemaContext";
import { EventFieldComponent } from "./event-field.component";

interface EventDetailDialogProps {
  open: boolean;
  eventName: string;
  formValues: Record<string, any>;
  onClose: () => void;
  handleChange: (eventName: string, field: string, value: any) => void;
  handlePublish: (eventName: string) => void;
}

export const EventDetailDialog: React.FC<EventDetailDialogProps> = ({
  open,
  eventName,
  formValues,
  onClose,
  handleChange,
  handlePublish,
}) => {
  const { schema } = useSchema();
  const resolveSchema = useResolveSchema();

  const resolvedSchema = useMemo(() => {
    if (!schema) return null;
    const rawSchema = (schema.components.schemas as any)[eventName];
    return resolveSchema(rawSchema);
  }, [eventName, schema, resolveSchema]);

  const handlePublishClick = () => {
    handlePublish(eventName);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {getDisplayEventName(eventName)}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3 }}>
        {hasProperties(resolvedSchema) ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {Object.entries(resolvedSchema.properties).map(([field, fieldSchema], index) => (
              <EventFieldComponent
                key={`${eventName}-${index}`}
                eventName={eventName}
                fieldName={field}
                fieldSchema={fieldSchema}
                formData={formValues}
                handleChange={handleChange}
              />
            ))}
          </Box>
        ) : (
          <Typography
            sx={{
              fontStyle: "italic",
              color: "text.secondary",
              textAlign: "center",
              py: 4,
            }}
          >
            No fields available for this event
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handlePublishClick}
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 500,
          }}
        >
          Publish Event
        </Button>
      </DialogActions>
    </Dialog>
  );
};
